import React, { useState } from "react";
import { getSalaryFilterOptions, saveLopUpload, getRecentLopUploads, getLopUpload, downloadPayrollReport, deleteLopUpload, importSalaryPayslips } from "../../api";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useLiteAllEmployeeDetails } from "../../hooks/useCandidate";

import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import DownloadIcon from '@mui/icons-material/Download';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  Container,
  Paper,
  Chip,
  useTheme,
  useMediaQuery,
  LinearProgress,
  CircularProgress,
  Modal,
  Autocomplete,
  MenuItem,
} from "@mui/material";
import { useGeneratePayslips, useGeneratePayslipPdfs } from "../../hooks/usePayslip";
import { useDownloadLopTemplate } from "../../hooks/useAttendance";
import { getCycleForPayrollMonth } from "../../utils/attendanceCycle";
import salryloadingimg from '../../assets/HandLoading.gif';
import img3 from '../../assets/images/2 3.png';
import * as XLSX from "xlsx";
import { useAlert } from "../../context/AlertContext";
import { useCompany } from "../../context/CompanyContext";
import EmployeeSelectionModal from "./EmployeeSelectionModal";
import SalaryFileMerge from "./SalaryFileMerge";
import GroupAddIcon from '@mui/icons-material/GroupAdd';

// ---- FORTHEYE soft-card tokens (shared with the leave/attendance dashboards) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';
const OUTLINED_BTN = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' };
const GRADIENT_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};
// Soft input look — rounded + light surface (matches the filter inputs portal-wide).
const SOFT_INPUT_SX = { '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } };

const PayrollProcess = () => {
  const theme = useTheme();
  useMediaQuery(theme.breakpoints.down('sm'));

  const {showError,showSuccess,showWarning}=useAlert();
  const company = useCompany();

  const { data: emp = [] } = useLiteAllEmployeeDetails();
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [file, setFile] = useState(null);
  const [excelData, setExcelData] = useState([]);
  // Filled "Salary Details" file → payslips directly (separate from the LOP flow)
  const [salaryFile, setSalaryFile] = useState(null);
  const { mutate: generatePayslips } = useGeneratePayslips();
  const { mutate: generatePayslipPdfs } = useGeneratePayslipPdfs();
  const { mutate: downloadLopTemplateMutate } = useDownloadLopTemplate();

  const [isLoading, setIsLoading] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState(null);

  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [, setWs1Data] = useState([]);
  const [, setWs2Data] = useState([]);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [missingEmployees, setMissingEmployees] = useState([]);
  
  // Filter States
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedTeamHead, setSelectedTeamHead] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Dynamic Options
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [managers, setManagers] = useState([]);
  const [teamHeads, setTeamHeads] = useState([]);

  // Recent (saved) LOP uploads — reuse without re-uploading
  const [recentUploads, setRecentUploads] = useState([]);
  const [selectedRecentId, setSelectedRecentId] = useState('');
  // Processed-report download — pick which batches to combine (for the month)
  const [reportSelIds, setReportSelIds] = useState([]);
  const [isReportLoading, setIsReportLoading] = useState(false);

  const loadRecentUploads = React.useCallback(async () => {
    try {
      setRecentUploads(await getRecentLopUploads());
    } catch (err) {
      console.error("Failed to load recent LOP uploads:", err);
    }
  }, []);

  // Fetch options + recent uploads on mount
  React.useEffect(() => {
    const fetchOptions = async () => {
      try {
        const data = await getSalaryFilterOptions();
        setBranches(["All", ...(data.branches || [])]);
        setLocations(["All", ...(data.locations || [])]);
        setManagers(["All", ...(data.managers || [])]);
        setTeamHeads(["All", ...(data.team_heads || [])]);
      } catch (err) {
        console.error("Failed to fetch payroll filter options:", err);
      }
    };
    fetchOptions();
    loadRecentUploads();
  }, [loadRecentUploads]);

  // Filesystem-safe label from the active selection (e.g. "vinod", "MDH",
  // "Bengaluru-B-Narayanapura-HO", or "All"). Used to name the LOP template,
  // the saved upload, and the processed report files so they all match.
  const buildLabelToken = () => {
    const parts = [selectedBranch, selectedTeamHead, selectedManager, selectedLocation]
      .filter((v) => v && v !== 'All');
    let raw = parts.join('-');
    if (!raw && selectedEmployees.length > 0) raw = `Selected-${selectedEmployees.length}`;
    const token = raw.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return token || 'All';
  };

  // Load a previously-saved LOP file: hydrate excelData + set its month so it's
  // ready to Process without re-uploading.
  const handleSelectRecent = async (id) => {
    setSelectedRecentId(id);
    if (!id) return;
    try {
      const detail = await getLopUpload(id);
      const rows = (detail.lop_data || []).map((r) => ({
        'Employee No': r.employee_no,
        'Name': r.name,
        'LOP Days': r.lop_days,
      }));
      setExcelData(rows);
      setSelectedEmployees([]); // process exactly the file's employees
      setFile(null);            // it's a reused file, not a fresh upload (won't re-save)
      // Auto-set the process month from the saved upload.
      const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December",
      ];
      const mIdx = monthNames.indexOf(detail.month);
      if (mIdx >= 0 && detail.year) {
        setSelectedMonth(`${detail.year}-${String(mIdx + 1).padStart(2, '0')}`);
        setMonth(detail.month);
        setYear(String(detail.year));
      }
      showSuccess(`Loaded LOP: ${detail.filename || detail.filters_label} (${detail.row_count} employees)`);
    } catch (err) {
      console.error("Failed to load LOP upload:", err);
      showError("Could not load that LOP file.");
    }
  };

  // Remove a saved LOP upload from the recent list.
  const handleDeleteRecent = async (id) => {
    try {
      await deleteLopUpload(id);
      if (String(selectedRecentId) === String(id)) setSelectedRecentId('');
      setReportSelIds((prev) => prev.filter((x) => x !== id));
      await loadRecentUploads();
      showSuccess("Removed from recent uploads.");
    } catch (err) {
      console.error("Failed to delete LOP upload:", err);
      showError("Could not remove that file.");
    }
  };

  // Saved LOP batches for the currently selected process month (for the report
  // download picker — team-head-wise or ALL).
  const monthUploads = recentUploads.filter(
    (u) => u.month === month && String(u.year) === String(year)
  );

  const handleDownloadReport = async () => {
    if (!month || !year) {
      showWarning("Select the Process Month first.");
      return;
    }
    if (!reportSelIds.length) {
      showWarning("Tick at least one batch to download.");
      return;
    }
    setIsReportLoading(true);
    try {
      const selected = monthUploads.filter((u) => reportSelIds.includes(u.id));
      const labels = Array.from(new Set(selected.map((u) => u.filters_label)));
      const label = labels.length <= 3 ? labels.join('-') : 'Combined';
      await downloadPayrollReport({ month, year, uploadIds: reportSelIds, label });
      showSuccess("Report downloaded.");
    } catch (err) {
      console.error("Failed to download report:", err);
      showError(err?.response?.data?.error || "Could not download the report.");
    } finally {
      setIsReportLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
          gap: 3,
        }}
      >
        <Box
          component="img"
          src={salryloadingimg}
          alt="Processing..."
          sx={{
            width: { xs: '300px', sm: '400px', md: '500px' },
            height: { xs: '300px', sm: '400px', md: '500px' },
            objectFit: 'contain',
          }}
        />
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ color: ACCENT, letterSpacing: '-0.01em' }}
        >
          Processing Payroll...
        </Typography>
        <LinearProgress
          sx={{
            width: '300px',
            height: '8px',
            borderRadius: '999px',
            bgcolor: `${ACCENT}1a`,
            '& .MuiLinearProgress-bar': {
              borderRadius: '999px',
              background: 'linear-gradient(90deg,#3b82f6,#2f6bff)',
            },
          }}
        />
      </Box>
    );
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const binaryString = evt.target.result;
      const wb = XLSX.read(binaryString, { type: 'binary' });
      const sheetName = wb.SheetNames[0];
      const ws = wb.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(ws);
      setExcelData(jsonData);
    };
    reader.readAsBinaryString(selectedFile);
  };

  const handleToggle = (employee) => {
    setSelectedEmployees((prevSelected) => {
      const exists = prevSelected.some((e) => e.employee_no === employee.employee_no);
      if (exists) {
        return prevSelected.filter((e) => e.employee_no !== employee.employee_no);
      }
      return [...prevSelected, employee];
    });
  };

  const handleDownloadTemplate = () => {
    // The LOP template now downloads BLANK — the admin fills LOP Days manually.
    // (LOP is no longer auto-calculated from attendance.) Salary is processed on
    // the calendar month, so blank LOP cells are treated as 0 on re-upload.
    if (!selectedMonth) {
      showWarning("Select the Process Month first — the blank LOP template is generated for that payroll month.");
      return;
    }
    const [yearValue, monthNumber] = selectedMonth.split('-').map((x) => parseInt(x, 10));
    const cycle = getCycleForPayrollMonth(monthNumber, yearValue).cycle;

    const params = { cycle };
    if (selectedBranch && selectedBranch !== "All") params.branch = selectedBranch;
    if (selectedTeamHead && selectedTeamHead !== "All") params.team_head = selectedTeamHead;
    if (selectedManager && selectedManager !== "All") params.manager = selectedManager;
    if (selectedLocation && selectedLocation !== "All") params.location = selectedLocation;
    if (selectedEmployees.length > 0) {
      params.employee_nos = selectedEmployees.map((e) => e.employee_no).join(',');
    }
    params.label = buildLabelToken();

    downloadLopTemplateMutate(params);
  };

  const handleSubmit = () => {
    let employeesWithLOP = [];

    if (selectedEmployees.length > 0) {
      // Use manually selected employees
      employeesWithLOP = selectedEmployees.map((employee) => {
        const matchedData = excelData.find((data) => {
          const empNo = data['Employee No'] || data['employee_no'] || data['Employee_No'];
          return String(empNo).trim() === String(employee.employee_no).trim();
        });
        
        const lopVal = matchedData ? (matchedData['LOP Days'] ?? matchedData['LOP'] ?? matchedData['LOP_days'] ?? matchedData['lop'] ?? 0) : 0;
        
        return {
          employee_no: String(employee.employee_no),
          lop_days: lopVal
        };
      });
    } else if (excelData.length > 0) {
      // If no manual selection, use all employees from the Excel file
      employeesWithLOP = excelData.map((data) => {
        const empNo = data['Employee No'] || data['employee_no'] || data['Employee_No'];
        const lopVal = data['LOP Days'] ?? data['LOP'] ?? data['LOP_days'] ?? data['lop'] ?? 0;

        return {
          employee_no: String(empNo),
          lop_days: lopVal
        };
      }).filter(e => e.employee_no && e.employee_no !== 'undefined'); // Filter out empty rows if any
    }

    if (employeesWithLOP.length === 0) {
      showWarning("No employees selected or found in Excel file.");
      return;
    }

    const payload = {
      employees: employeesWithLOP,
      month: month,
      year: year
    };
    if(!month || !year){
      showWarning("Have To Select The Month To Process")
      return
    }

    setIsLoading(true);
    generatePayslips(payload, {
      onMutate: () => {
        setIsLoading(true);
      },
      onSuccess: (data) => {
        // Process now only calculates + builds the Salary Details report; it does NOT
        // create payslips. Draft payslips are created by "Generate from Salary File".
        showSuccess('Payroll calculated — Salary Details report is ready. Upload the filled Salary Details file below to create draft payslips.');

        // Save the just-processed LOP file so it can be reused later. Only for a
        // fresh upload — reusing a saved file (file === null) won't re-save.
        if (file) {
          const lopData = excelData.map((d) => ({
            employee_no: String(d['Employee No'] ?? d['employee_no'] ?? d['Employee_No'] ?? '').trim(),
            name: d['Name'] ?? d['name'] ?? '',
            lop_days: d['LOP Days'] ?? d['LOP'] ?? d['LOP_days'] ?? d['lop'] ?? 0,
          })).filter((r) => r.employee_no && r.employee_no !== 'undefined');
          saveLopUpload({ file, month, year, filtersLabel: buildLabelToken(), lopData })
            .then(() => loadRecentUploads())
            .catch((err) => console.error('Failed to save LOP upload:', err));
        }

        if (data?.ws1_data && data?.ws2_data) {
          const excel1 = data?.ws1_data;
          const excel2 = data?.ws2_data;
          setWs1Data(excel1);
          setWs2Data(excel2);

          // Processed reports carry the same selection label as the LOP file.
          const lblToken = buildLabelToken();
          const lbl = lblToken && lblToken !== 'All' ? `_${lblToken}` : '';

          // Auto-download Bank and Salary Details
          showSuccess('Downloading reports...');
          setTimeout(() => {
            exportToExcel(excel1, `BankDetails${lbl}_${month}_${year}.xlsx`);
          }, 500);
          setTimeout(() => {
            exportToExcel(excel2, `SalaryDetails${lbl}_${month}_${year}.xlsx`);
          }, 1500);
        }
      },
      onError: (error) => {
        console.error("Mutation error:", error);
        if (error?.response?.data?.missing_employees) {
          setMissingEmployees(error.response.data.missing_employees);
          setOpenErrorModal(true);
        } else {
          // The 500 body may be an HTML error page (no JSON .error), which used to
          // render "Error:undefined". Prefer a JSON error/detail, else the HTTP status,
          // else the network message — never dump raw HTML at the user.
          const data = error?.response?.data;
          const jsonMsg = data && typeof data === 'object' ? (data.error || data.detail) : null;
          const msg = jsonMsg
            || (error?.response?.status
                  ? `Server error (${error.response.status}). Please try again or check the server logs.`
                  : (error?.message || 'Something went wrong. Please try again.'));
          showError(`Error: ${msg}`);
        }
      },
      onSettled: () => {
        setIsLoading(false);
      },
    });
  };

  const handleGeneratePdfs = () => {
    if(!month || !year){
      showWarning("Please select Month and Year.")
      return
    }

    const payload = {
      month: month,
      year: year
    };

    setIsLoading(true);
    generatePayslipPdfs(payload, {
      onSuccess: (data) => {
        showSuccess(data.message || 'PDFs generated successfully!');
        if(data.errors && data.errors.length > 0){
             showWarning(`Some errors occurred: ${data.errors.join(", ")}`);
        }
      },
      onError: (error) => {
        console.error("PDF Generation error:", error);
        showError(`Error: ${error?.response?.data?.error || "Failed to generate PDFs"}`);
      },
      onSettled: () => {
        setIsLoading(false);
      },
    });
  };

  // Pick a filled "Salary Details" Excel (the file this page downloads). It is
  // sent as-is to the backend — no in-browser parsing needed.
  const handleSalaryFileChange = (e) => {
    setSalaryFile(e.target.files[0] || null);
  };

  // Generate payslips DIRECTLY from the uploaded Salary Details file. Values are
  // used verbatim (no LOP recalculation); the payslips then go through the normal
  // flow (Generate PDFs / Publish / Email).
  const handleImportSalary = () => {
    if (!salaryFile) {
      showWarning("Choose a Salary Details Excel file first.");
      return;
    }
    if (!month || !year) {
      showWarning("Select the Process Month first.");
      return;
    }
    setIsLoading(true);
    importSalaryPayslips({ file: salaryFile, month, year })
      .then((data) => {
        showSuccess(`Generated ${data.created} payslip(s) from the salary file. You can now Generate PDFs.`);
        if (data.unmatched_employee_nos && data.unmatched_employee_nos.length > 0) {
          showWarning(`Skipped ${data.skipped} unmatched employee(s): ${data.unmatched_employee_nos.join(', ')}`);
        }
        // Food Allowance is HO-file only: any amount on a non-HO row was kept at 0
        // and taken off that payslip's gross / net.
        if (data.food_removed_count > 0) {
          const who = data.food_removed.map((f) => f.employee_no).join(', ');
          showWarning(`Food Allowance kept at 0 for ${data.food_removed_count} non-HO employee(s) (HO file only): ${who}`);
        }
      })
      .catch((err) => {
        console.error("Salary import error:", err);
        showError(`Error: ${err?.response?.data?.error || "Failed to import the salary file"}`);
      })
      .finally(() => setIsLoading(false));
  };


  const exportToExcel = (data, fileName) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, fileName);
  };

  const handleChange = (event) => {
    const rawVal = event.target.value;
    setSelectedMonth(rawVal);
    
    if (!rawVal) {
      setMonth('');
      setYear('');
      return;
    }

    const response = rawVal.split('-');
    const yearValue = response[0];
    const monthNumber = parseInt(response[1], 10);

    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];

    const monthName = monthNames[monthNumber - 1];
    setMonth(monthName);
    setYear(yearValue);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Container maxWidth="xl" disableGutters>
        {/* Header card */}
        <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
              <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <AccountBalanceIcon />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                  Payroll Processing
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Select employees, process LOP and generate payslips for the month
                </Typography>
              </Box>
            </Box>
            {selectedEmployees.length > 0 && (
              <Chip
                label={`${selectedEmployees.length} Selected`}
                onDelete={() => setSelectedEmployees([])}
                sx={{ fontWeight: 700, borderRadius: '8px', bgcolor: '#eef4ff', color: ACCENT, border: 'none', '& .MuiChip-deleteIcon': { color: ACCENT } }}
              />
            )}
          </Box>
        </Paper>

        {/* Workflow Steps */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 3 } }}>
          
          {/* STEP 1: Selection & Preparation */}
          <Paper elevation={0} sx={CARD_SX}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, gap: 1.5 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: '11px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.95rem', flexShrink: 0 }}>1</Box>
              <Typography sx={{ fontWeight: 800, color: '#111827', fontSize: '1.05rem' }}>Selection & Preparation</Typography>
            </Box>

            {/* Step 1 Content - More Compact */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <Autocomplete
                options={teamHeads}
                size="small"
                value={selectedTeamHead}
                onChange={(event, newValue) => setSelectedTeamHead(newValue)}
                renderInput={(params) => <TextField {...params} label="Team Head" />}
                sx={{ width: 220, ...SOFT_INPUT_SX }}
              />
              <Autocomplete
                options={branches}
                size="small"
                value={selectedBranch}
                onChange={(event, newValue) => setSelectedBranch(newValue)}
                renderInput={(params) => <TextField {...params} label="Branch" />}
                sx={{ width: 220, ...SOFT_INPUT_SX }}
              />
              <Autocomplete
                options={managers}
                size="small"
                value={selectedManager}
                onChange={(event, newValue) => setSelectedManager(newValue)}
                renderInput={(params) => <TextField {...params} label="Manager" />}
                sx={{ width: 220, ...SOFT_INPUT_SX }}
              />
              <Autocomplete
                options={locations}
                size="small"
                value={selectedLocation}
                onChange={(event, newValue) => setSelectedLocation(newValue)}
                renderInput={(params) => <TextField {...params} label="Region/Location" />}
                sx={{ width: 220, ...SOFT_INPUT_SX }}
              />
              <TextField
                label="Process Month"
                type="month"
                size="small"
                value={selectedMonth}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 180, ...SOFT_INPUT_SX }}
              />

              <Box sx={{ flexGrow: 1 }} />

              {/* Removed redundant Select All Filtered button */}
              <Button
                variant="outlined"
                startIcon={<GroupAddIcon />}
                onClick={() => setIsModalOpen(true)}
                sx={{ ...OUTLINED_BTN, height: '40px' }}
              >
                Manual Select
              </Button>
              <Button
                variant="contained"
                disableElevation
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
                sx={{ ...GRADIENT_BTN, height: '40px' }}
              >
                LOP Template
              </Button>
            </Box>
          </Paper>

              <EmployeeSelectionModal 
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                employees={emp}
                selectedEmployees={selectedEmployees}
                onToggle={handleToggle}
                onClearAll={() => setSelectedEmployees([])}
              />

          {/* STEP 2: Execution & Validation */}
          <Paper elevation={0} sx={CARD_SX}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, gap: 1.5 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: '11px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.95rem', flexShrink: 0 }}>2</Box>
              <Typography sx={{ fontWeight: 800, color: '#111827', fontSize: '1.05rem' }}>Payroll Execution</Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.5fr 1fr' }, gap: 4, alignItems: 'center' }}>
              <Box>
                {/* Reuse a previously processed LOP file (no re-upload) */}
                {recentUploads.length > 0 && (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Reuse a recent LOP upload"
                    value={selectedRecentId}
                    onChange={(e) => handleSelectRecent(e.target.value)}
                    sx={{ mb: 2, ...SOFT_INPUT_SX }}
                    SelectProps={{
                      renderValue: (val) => {
                        if (!val) return '';
                        const u = recentUploads.find((x) => x.id === val);
                        return u ? `${u.month} ${u.year} · ${u.filters_label} · ${u.row_count} emp` : '';
                      },
                    }}
                  >
                    <MenuItem value="">— Upload a new file instead —</MenuItem>
                    {recentUploads.map((u) => (
                      <MenuItem key={u.id} value={u.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <span>{u.month} {u.year} · {u.filters_label} · {u.row_count} emp · {u.uploaded_at}</span>
                        <IconButton
                          size="small"
                          edge="end"
                          aria-label="Remove this saved upload"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => { e.stopPropagation(); handleDeleteRecent(u.id); }}
                          sx={{ ml: 1 }}
                        >
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </IconButton>
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <input
                  id="file-input-workflow"
                  style={{ display: 'none' }}
                  type="file"
                  onChange={handleFileChange}
                  accept=".xlsx,.xls"
                />
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <label htmlFor="file-input-workflow" style={{ flexGrow: 1 }}>
                    <Button
                      component="span"
                      fullWidth
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      sx={{ ...OUTLINED_BTN, height: '48px' }}
                    >
                      {file ? file.name : 'Upload LOP Excel'}
                    </Button>
                  </label>
                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={<PlayCircleFilledIcon />}
                    onClick={handleSubmit}
                    disabled={(!selectedEmployees.length && !excelData.length) || !month}
                    sx={{ ...GRADIENT_BTN, height: '48px', flexBasis: '100%' }}
                  >
                    Process {(selectedEmployees.length || excelData.length) > 0 ? `(${selectedEmployees.length || excelData.length} Emps)` : ''}
                  </Button>
                </Box>
              </Box>

              <Box sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
                <Box
                  component="img"
                  src={img3}
                  alt="Process"
                  sx={{ width: '100%', maxWidth: '120px', opacity: 0.8 }}
                />
              </Box>
            </Box>

            {/* Download processed Bank + Salary report — tick the batches to combine */}
            {monthUploads.length > 0 && (
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px dashed #e5e7eb', display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ minWidth: 200, color: '#6b7280' }}>
                  Download processed report for {month} {year}:
                </Typography>
                <TextField
                  select
                  size="small"
                  label="Select batches to combine"
                  value={reportSelIds}
                  onChange={(e) => setReportSelIds(e.target.value)}
                  SelectProps={{
                    multiple: true,
                    renderValue: (sel) => (
                      sel.length === 0 ? ''
                        : sel.length === monthUploads.length ? 'All batches'
                          : `${sel.length} selected`
                    ),
                  }}
                  sx={{ minWidth: 320, ...SOFT_INPUT_SX }}
                >
                  {monthUploads.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      <Checkbox size="small" checked={reportSelIds.indexOf(u.id) > -1} />
                      {u.filters_label} · {u.row_count} emp · {u.uploaded_at}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={isReportLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <DownloadIcon />}
                  onClick={handleDownloadReport}
                  disabled={isReportLoading || !reportSelIds.length}
                  sx={{ ...GRADIENT_BTN, height: '40px' }}
                >
                  Download {reportSelIds.length > 1 ? 'Combined ' : ''}Report
                </Button>
              </Box>
            )}
          </Paper>

          {/* STEP 3: Finalization */}
          <Paper elevation={0} sx={CARD_SX}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, gap: 1.5 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: '11px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.95rem', flexShrink: 0 }}>3</Box>
              <Typography sx={{ fontWeight: 800, color: '#111827', fontSize: '1.05rem' }}>Payslip Finalization</Typography>
            </Box>

            {/* Upload the four team salary files (HO / Vinod / Sathish / MDH) → merge → download one file. */}
            {company.code?.toUpperCase() === 'ATS001' && (
              <SalaryFileMerge month={month} year={year} />
            )}

            {/* Upload a filled Salary Details file → Generate Payslip creates the DRAFT
                payslips (values used as-is, no LOP recalculation); Generate PDFs then
                renders them. This upload is the only path that creates payslips. */}
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 1.5 }}>
              Upload a filled <b>Salary Details</b> file and <b>Generate Payslip</b> to create the draft payslips (values used as-is, no LOP recalculation), then <b>Generate PDFs</b>.
            </Typography>
            <input
              id="salary-file-input"
              style={{ display: 'none' }}
              type="file"
              onChange={handleSalaryFileChange}
              accept=".xlsx"
            />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <label htmlFor="salary-file-input">
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  sx={{
                    ...OUTLINED_BTN,
                    height: '48px',
                    width: 300,
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}
                >
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {salaryFile ? salaryFile.name : 'Upload Salary Details Excel'}
                  </Box>
                </Button>
              </label>
              <Button
                variant="contained"
                disableElevation
                startIcon={<PlayCircleFilledIcon />}
                onClick={handleImportSalary}
                disabled={isLoading}
                sx={{ ...GRADIENT_BTN, height: '48px', minWidth: '200px' }}
              >
                Generate Payslip
              </Button>
              <Button
                variant="contained"
                disableElevation
                startIcon={<DescriptionIcon />}
                onClick={handleGeneratePdfs}
                disabled={!month || !year}
                sx={{ ...GRADIENT_BTN, height: '48px', minWidth: '200px' }}
              >
                Generate PDFs
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>

      {/* Error Modal for Missing Salary Details */}
      <Modal
        open={openErrorModal}
        onClose={() => setOpenErrorModal(false)}
        aria-labelledby="error-modal-title"
        aria-describedby="error-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            bgcolor: '#fff',
            borderRadius: '22px',
            boxShadow: '0 24px 60px rgba(17,24,39,0.18)',
            p: 4,
            outline: 'none',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                bgcolor: '#fef2f2',
                color: '#ef4444',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CloseIcon />
            </Box>
            <Typography id="error-modal-title" variant="h6" component="h2" sx={{ fontWeight: 800, color: '#ef4444' }}>
              Salary Details Missing
            </Typography>
          </Box>

          <Typography id="error-modal-description" sx={{ mb: 2, color: '#6b7280' }}>
            The following employees do not have <strong>Salary Master</strong> or <strong>CTC Items</strong> configured. Payslips cannot be generated for them.
          </Typography>

          <Paper
            elevation={0}
            sx={{
              maxHeight: 200,
              overflow: 'auto',
              mb: 3,
              bgcolor: '#f8fafc',
              borderRadius: '14px',
              border: '1px solid #eef0f4',
            }}
          >
            <List dense>
              {missingEmployees.map((name, index) => (
                <ListItem key={index} divider={index !== missingEmployees.length - 1}>
                   <ListItemText primary={name} primaryTypographyProps={{ fontWeight: 600, color: '#111827' }} />
                </ListItem>
              ))}
            </List>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              disableElevation
              onClick={() => setOpenErrorModal(false)}
              sx={{ ...GRADIENT_BTN, px: 3 }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default PayrollProcess;




// {/* <div style={{ width: '100%' }}>
// <Typography variant="body" sx={{ marginBottom: '5px',width:'100%',display:'flex',justifyContent:'end',alignItems:'center' }}>
//   Last Processed Log
// </Typography>

// {/* Dropdown to select the month */}
// <div style={{ marginBottom: '10px' }}>
//   <Typography variant="body1">Select Month:</Typography>
//  <div style={{display:'flex',width:'100%'}}>
// <div style={{flexBasis:'50%'}}>
// <FormControl sx={{marginTop:'6px'}} >
//     <InputLabel id="month-select-label">Month</InputLabel>
//     <Select
//       labelId="month-select-label"
//       value={selectedMonth || ''}
//       onChange={(e) => handleMonthSelect(e.target.value)}
//       label="Month"
//        sx={{          
//                             minWidth: '100px',
//                             maxHeight: '39px',
//                             backgroundColor:
//                               theme.palette.mode === 'dark' ? grey[900] : '#fff',
//                             borderRadius: '8px',
//                             border: `1px solid ${
//                               theme.palette.mode === 'dark' ? grey[700] : grey[200]
//                             }`,
//                             boxShadow: `0px 2px 2px ${
//                               theme.palette.mode === 'dark' ? grey[900] : grey[50]
//                             }`,
//                             '&:hover': {
//                               '&& fieldset': {
//                                 border: `1px solid ${blue[400]}`,
//                               },
//                             },
//                           }}
//     >
//       <MenuItem value="">All</MenuItem>
//       {uniqueMonths.map((month) => (
//         <MenuItem key={month} value={month}>
//           {month}
//         </MenuItem>
//       ))}
//     </Select>
//   </FormControl>
// </div>
// <div style={{flexBasis:'50%'}}>
// <StyledInput  value={searchName}
//     onChange={handleSearchNameChange}/>
// </div>
  
//  </div>
// </div>
// <div style={{ height: '150px', overflowY: 'scroll',display:'flex' }}>
// {/* Table displaying the filtered payslip data */}
// <table
//   border="1"
//   style={{
//     width: '100%',
//     textAlign: 'left',
//     borderCollapse: 'collapse',
   
//   }}
// >
//   <thead>
//     <tr>
//       <th>Payroll</th>
//       <th>Description</th>
//     </tr>
//   </thead>
//   <tbody>
//     {filteredPayslipData.map((payslip, index) => (
//       <tr key={index}>
//         <td>{`${payslip.month} ${payslip.year}`}</td>
//         <td>{payslip.employee} payslip has been Generated</td>
//       </tr>
//     ))}
//   </tbody>
// </table>
// </div>



// </div> */}
